import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { 
  ArrowLeft, ArrowRight, Zap, Flame, User, Building2, MapPin, FileText, 
  CheckCircle, PenTool, Upload, AlertTriangle, Info, Home, Calendar,
  Phone, Mail, Hash, Loader2, Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";

const STEPS = [
  { id: 1, title: "Tipo Fornitura", icon: Zap },
  { id: 2, title: "Dati Cliente", icon: User },
  { id: 3, title: "Dati Utenza", icon: MapPin },
  { id: 4, title: "Dichiarazioni", icon: FileText },
  { id: 5, title: "Dettagli", icon: Home },
  { id: 6, title: "Firma", icon: PenTool },
  { id: 7, title: "Conferma", icon: CheckCircle },
];

const initialFormData = {
  // Step 1 - Tipo Fornitura
  tipo_fornitura: "", // "luce" o "gas"
  
  // Step 2 - Dati Cliente
  tipo_cliente: "privato", // "privato" o "azienda"
  cognome: "",
  nome: "",
  ragione_sociale: "",
  codice_fiscale: "",
  partita_iva: "",
  codice_ateco: "",
  telefono: "",
  email: "",
  
  // Domicilio Fiscale
  domicilio_via: "",
  domicilio_numero: "",
  domicilio_cap: "",
  domicilio_comune: "",
  domicilio_provincia: "",
  
  // Step 3 - Dati Utenza
  codice_pod_pdr: "", // POD per luce, PDR per gas
  matricola_contatore: "",
  fornitura_via: "",
  fornitura_numero: "",
  fornitura_cap: "",
  fornitura_comune: "",
  fornitura_provincia: "",
  
  // Step 4 - Dichiarazioni Immobile
  titolo_possesso: "proprietario", // proprietario, locatario, comodatario, altro
  titolo_altro_specificare: "",
  catasto_foglio: "",
  catasto_particella: "",
  catasto_subalterno: "",
  
  // Dichiarazioni Edilizie
  dichiarazione_edilizia: "dopo_1977", // dopo_1977, prima_1977_no_modifiche, prima_1977_modifiche, sanatoria
  permesso_comune: "",
  permesso_numero: "",
  permesso_data: "",
  
  // Step 5 - Dettagli Fornitura
  // Per LUCE
  tensione: "bassa_monofase", // bassa_monofase, bassa_trifase, media
  tipo_fornitura_dettaglio: "ordinaria", // ordinaria, stagionale_ricorrente, stagionale_non_ricorrente, straordinaria
  uso_fornitura: "residente", // residente, non_residente, altri_usi, illuminazione_pubblica, cantieri
  potenza_richiesta: "3",
  data_inizio: "",
  data_fine: "",
  
  // Per GAS
  classe_contatore: "g4", // g4, g6, superiore_g6
  uso_gas: "cottura_acqua", // cottura_acqua, riscaldamento, uso_tecnologico
  
  // Step 6 - Firma
  firma_data: "",
  firma_luogo: "",
  firma_immagine: "", // Base64 della firma
  
  // Consensi
  consenso_privacy: false,
  consenso_marketing: false,
  
  // Document
  documento_tipo: "carta_identita",
  documento_numero: "",
  documento_scadenza: "",
};

export default function ActivationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const canvasRef = useRef(null);
  const contextRef = useRef(null);

  // Pre-select type from URL params
  useEffect(() => {
    const tipo = searchParams.get("tipo");
    if (tipo === "luce" || tipo === "gas") {
      setFormData(prev => ({ ...prev, tipo_fornitura: tipo }));
    }
  }, [searchParams]);

  // Initialize signature canvas
  useEffect(() => {
    if (currentStep === 6 && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = 200 * 2;
      canvas.style.width = `${canvas.offsetWidth}px`;
      canvas.style.height = "200px";
      
      const context = canvas.getContext("2d");
      context.scale(2, 2);
      context.lineCap = "round";
      context.strokeStyle = "#1d4ed8";
      context.lineWidth = 2;
      contextRef.current = context;
      
      // Fill white background
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [currentStep]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const startDrawing = ({ nativeEvent }) => {
    if (!contextRef.current) return;
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing || !contextRef.current) return;
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!contextRef.current || !canvasRef.current) return;
    contextRef.current.closePath();
    setIsDrawing(false);
    // Save signature as base64
    const signatureData = canvasRef.current.toDataURL("image/png");
    updateField("firma_immagine", signatureData);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;
    context.fillStyle = "white";
    context.fillRect(0, 0, canvas.width, canvas.height);
    updateField("firma_immagine", "");
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.tipo_fornitura !== "";
      case 2:
        if (formData.tipo_cliente === "privato") {
          return formData.cognome && formData.nome && formData.codice_fiscale && formData.telefono;
        } else {
          return formData.ragione_sociale && formData.partita_iva && formData.telefono;
        }
      case 3:
        return formData.codice_pod_pdr && formData.matricola_contatore && 
               formData.fornitura_via && formData.fornitura_comune;
      case 4:
        return formData.titolo_possesso && formData.dichiarazione_edilizia;
      case 5:
        return true; // Optional fields
      case 6:
        return formData.firma_immagine && formData.consenso_privacy && 
               formData.documento_numero;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const [requestId, setRequestId] = useState(null);

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      toast.error("Completa la firma e accetta i termini");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/activation-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error("Errore di comunicazione");
      }

      if (!response.ok) {
        throw new Error(data.detail || "Errore nell'invio della richiesta");
      }

      // Save request_id for PDF download
      setRequestId(data.request_id);
      toast.success("Richiesta inviata con successo!");
      setCurrentStep(7);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!requestId) return;
    
    try {
      toast.loading("Generazione PDF in corso...");
      const response = await fetch(`${API}/activation-requests/${requestId}/pdf`, {
        credentials: "include"
      });
      
      if (!response.ok) {
        throw new Error("Errore nella generazione del PDF");
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto_${requestId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("PDF scaricato!");
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            <img src={LOGO_URL} alt="8energy" className="h-8" />
          </div>
          <div className="flex items-center gap-2">
            {formData.tipo_fornitura === "luce" ? (
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <Zap className="w-4 h-4" /> Luce
              </span>
            ) : formData.tipo_fornitura === "gas" ? (
              <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <Flame className="w-4 h-4" /> Gas
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10" />
            <div 
              className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500 -z-10"
              style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
            />
            
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive ? "bg-primary text-white scale-110 shadow-lg" :
                    isCompleted ? "bg-green-500 text-white" :
                    "bg-gray-200 text-gray-500"
                  }`}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-2 font-medium hidden sm:block ${
                    isActive ? "text-primary" : isCompleted ? "text-green-600" : "text-gray-400"
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step 1: Tipo Fornitura */}
            {currentStep === 1 && (
              <Card>
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">Seleziona il Tipo di Fornitura</CardTitle>
                  <CardDescription>Per quale servizio vuoi richiedere l'attivazione?</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <button
                      type="button"
                      onClick={() => updateField("tipo_fornitura", "luce")}
                      className={`p-8 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                        formData.tipo_fornitura === "luce"
                          ? "border-yellow-500 bg-yellow-50 shadow-lg shadow-yellow-200"
                          : "border-gray-200 hover:border-yellow-300 bg-white"
                      }`}
                    >
                      <div className="w-20 h-20 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Zap className={`w-10 h-10 ${formData.tipo_fornitura === "luce" ? "text-yellow-600" : "text-yellow-500"}`} />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Energia Elettrica</h3>
                      <p className="text-sm text-muted-foreground">Nuova attivazione o allaccio luce</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => updateField("tipo_fornitura", "gas")}
                      className={`p-8 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                        formData.tipo_fornitura === "gas"
                          ? "border-orange-500 bg-orange-50 shadow-lg shadow-orange-200"
                          : "border-gray-200 hover:border-orange-300 bg-white"
                      }`}
                    >
                      <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                        <Flame className={`w-10 h-10 ${formData.tipo_fornitura === "gas" ? "text-orange-600" : "text-orange-500"}`} />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Gas Naturale</h3>
                      <p className="text-sm text-muted-foreground">Nuova attivazione o allaccio gas</p>
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Dati Cliente */}
            {currentStep === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-6 h-6 text-primary" />
                    Dati del Cliente
                  </CardTitle>
                  <CardDescription>Inserisci i tuoi dati anagrafici</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Banner Solo Domestico */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Offerta riservata a utenze domestiche</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Questo form di attivazione online è disponibile esclusivamente per clienti privati con utenze domestiche. 
                        Per utenze business, contatta i nostri agenti al <strong>+39 392 745 1191</strong> o via email a <strong>info@8energysolutions.com</strong>
                      </p>
                    </div>
                  </div>

                  {/* Tipo Cliente - Solo Privato (hidden field) */}
                  <input type="hidden" value="privato" />
                  
                  {/* Dati Privato */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cognome *</Label>
                      <Input 
                        value={formData.cognome}
                        onChange={(e) => updateField("cognome", e.target.value)}
                        placeholder="Rossi"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome *</Label>
                      <Input 
                        value={formData.nome}
                        onChange={(e) => updateField("nome", e.target.value)}
                        placeholder="Mario"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Codice Fiscale *</Label>
                      <Input 
                        value={formData.codice_fiscale}
                        onChange={(e) => updateField("codice_fiscale", e.target.value.toUpperCase())}
                        placeholder="RSSMRA80A01H501Z"
                        maxLength={16}
                        className="uppercase"
                      />
                    </div>
                  </div>

                  {/* Contatti */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Telefono *</Label>
                      <Input 
                        type="tel"
                        value={formData.telefono}
                        onChange={(e) => updateField("telefono", e.target.value)}
                        placeholder="+39 333 1234567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                      <Input 
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        placeholder="mario@email.com"
                      />
                    </div>
                  </div>

                  {/* Domicilio Fiscale */}
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Home className="w-5 h-5 text-primary" />
                      Domicilio Fiscale
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Via/Piazza</Label>
                        <Input 
                          value={formData.domicilio_via}
                          onChange={(e) => updateField("domicilio_via", e.target.value)}
                          placeholder="Via Roma"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>N°</Label>
                        <Input 
                          value={formData.domicilio_numero}
                          onChange={(e) => updateField("domicilio_numero", e.target.value)}
                          placeholder="123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CAP</Label>
                        <Input 
                          value={formData.domicilio_cap}
                          onChange={(e) => updateField("domicilio_cap", e.target.value)}
                          placeholder="00100"
                          maxLength={5}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Comune</Label>
                        <Input 
                          value={formData.domicilio_comune}
                          onChange={(e) => updateField("domicilio_comune", e.target.value)}
                          placeholder="Roma"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Provincia</Label>
                        <Input 
                          value={formData.domicilio_provincia}
                          onChange={(e) => updateField("domicilio_provincia", e.target.value.toUpperCase())}
                          placeholder="RM"
                          maxLength={2}
                          className="uppercase"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Dati Utenza */}
            {currentStep === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-primary" />
                    Dati dell'Utenza
                  </CardTitle>
                  <CardDescription>
                    Informazioni sul punto di fornitura {formData.tipo_fornitura === "luce" ? "(POD)" : "(PDR)"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Alert Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Dove trovo questi dati?</p>
                      <p>Il codice {formData.tipo_fornitura === "luce" ? "POD" : "PDR"} e la matricola del contatore sono riportati sulla bolletta del tuo attuale fornitore o sul display del contatore.</p>
                    </div>
                  </div>

                  {/* Codici Utenza */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Hash className="w-4 h-4" />
                        Codice {formData.tipo_fornitura === "luce" ? "POD" : "PDR"} *
                      </Label>
                      <Input 
                        value={formData.codice_pod_pdr}
                        onChange={(e) => updateField("codice_pod_pdr", e.target.value.toUpperCase())}
                        placeholder={formData.tipo_fornitura === "luce" ? "IT001E12345678" : "12345678901234"}
                        className="uppercase font-mono"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.tipo_fornitura === "luce" 
                          ? "Inizia con IT e contiene 14 caratteri" 
                          : "Codice di 14 cifre"}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Matricola Contatore *</Label>
                      <Input 
                        value={formData.matricola_contatore}
                        onChange={(e) => updateField("matricola_contatore", e.target.value)}
                        placeholder="A12345678"
                        className="font-mono"
                      />
                    </div>
                  </div>

                  {/* Indirizzo Fornitura */}
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-4">Indirizzo della Fornitura *</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Via/Piazza *</Label>
                        <Input 
                          value={formData.fornitura_via}
                          onChange={(e) => updateField("fornitura_via", e.target.value)}
                          placeholder="Via Garibaldi"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>N° *</Label>
                        <Input 
                          value={formData.fornitura_numero}
                          onChange={(e) => updateField("fornitura_numero", e.target.value)}
                          placeholder="10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CAP *</Label>
                        <Input 
                          value={formData.fornitura_cap}
                          onChange={(e) => updateField("fornitura_cap", e.target.value)}
                          placeholder="50100"
                          maxLength={5}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Comune *</Label>
                        <Input 
                          value={formData.fornitura_comune}
                          onChange={(e) => updateField("fornitura_comune", e.target.value)}
                          placeholder="Firenze"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Provincia (sigla) *</Label>
                        <Input 
                          value={formData.fornitura_provincia}
                          onChange={(e) => updateField("fornitura_provincia", e.target.value.toUpperCase())}
                          placeholder="FI"
                          maxLength={2}
                          className="uppercase"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Checkbox indirizzo uguale */}
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="same_address"
                      onCheckedChange={(checked) => {
                        if (checked) {
                          updateField("fornitura_via", formData.domicilio_via);
                          updateField("fornitura_numero", formData.domicilio_numero);
                          updateField("fornitura_cap", formData.domicilio_cap);
                          updateField("fornitura_comune", formData.domicilio_comune);
                          updateField("fornitura_provincia", formData.domicilio_provincia);
                        }
                      }}
                    />
                    <label htmlFor="same_address" className="text-sm cursor-pointer">
                      L'indirizzo di fornitura è uguale al domicilio fiscale
                    </label>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Dichiarazioni Immobile */}
            {currentStep === 4 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-6 h-6 text-primary" />
                    Dichiarazioni sull'Immobile
                  </CardTitle>
                  <CardDescription>
                    Dichiarazioni obbligatorie ai sensi della normativa vigente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Attenzione</p>
                      <p>Le dichiarazioni seguenti sono rese ai sensi del D.P.R. 445/2000. Dichiarazioni mendaci sono perseguibili penalmente.</p>
                    </div>
                  </div>

                  {/* Titolo Possesso */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Titolo di Possesso dell'Immobile *</Label>
                    <p className="text-sm text-muted-foreground">
                      Ai sensi dell'art. 5 della Legge n. 80/2014 (Lotta all'occupazione abusiva)
                    </p>
                    <RadioGroup
                      value={formData.titolo_possesso}
                      onValueChange={(v) => updateField("titolo_possesso", v)}
                      className="grid grid-cols-2 gap-3"
                    >
                      {[
                        { value: "proprietario", label: "Proprietario" },
                        { value: "locatario", label: "Locatario" },
                        { value: "comodatario", label: "Comodatario" },
                        { value: "altro", label: "Altro titolo" },
                      ].map((option) => (
                        <div key={option.value} className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.titolo_possesso === option.value ? "border-primary bg-primary/5" : "border-gray-200"
                        }`}>
                          <RadioGroupItem value={option.value} id={option.value} className="sr-only" />
                          <label htmlFor={option.value} className="cursor-pointer font-medium">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                    
                    {formData.titolo_possesso === "altro" && (
                      <Input 
                        value={formData.titolo_altro_specificare}
                        onChange={(e) => updateField("titolo_altro_specificare", e.target.value)}
                        placeholder="Specificare il titolo..."
                        className="mt-2"
                      />
                    )}
                  </div>

                  {/* Dati Catastali */}
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-4">Estremi Catastali dell'Immobile</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Foglio</Label>
                        <Input 
                          value={formData.catasto_foglio}
                          onChange={(e) => updateField("catasto_foglio", e.target.value)}
                          placeholder="123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Particella</Label>
                        <Input 
                          value={formData.catasto_particella}
                          onChange={(e) => updateField("catasto_particella", e.target.value)}
                          placeholder="456"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Subalterno</Label>
                        <Input 
                          value={formData.catasto_subalterno}
                          onChange={(e) => updateField("catasto_subalterno", e.target.value)}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dichiarazioni Edilizie */}
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-2">Dichiarazioni ai sensi del DPR 380/2001 *</h4>
                    <p className="text-sm text-muted-foreground mb-4">"Testo Unico Edilizia"</p>
                    
                    <RadioGroup
                      value={formData.dichiarazione_edilizia}
                      onValueChange={(v) => updateField("dichiarazione_edilizia", v)}
                      className="space-y-3"
                    >
                      <div className={`p-4 rounded-lg border-2 ${
                        formData.dichiarazione_edilizia === "dopo_1977" ? "border-primary bg-primary/5" : "border-gray-200"
                      }`}>
                        <RadioGroupItem value="dopo_1977" id="dopo_1977" className="sr-only" />
                        <label htmlFor="dopo_1977" className="cursor-pointer">
                          <p className="font-medium">Edificata dopo il 28/01/1977</p>
                          <p className="text-sm text-muted-foreground">Conformemente al permesso di costruire / concessione edilizia / D.I.A.</p>
                        </label>
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${
                        formData.dichiarazione_edilizia === "prima_1977_no_modifiche" ? "border-primary bg-primary/5" : "border-gray-200"
                      }`}>
                        <RadioGroupItem value="prima_1977_no_modifiche" id="prima_1977_no_modifiche" className="sr-only" />
                        <label htmlFor="prima_1977_no_modifiche" className="cursor-pointer">
                          <p className="font-medium">Edificata prima del 28/01/1977</p>
                          <p className="text-sm text-muted-foreground">E successivamente non ha subito modificazioni</p>
                        </label>
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${
                        formData.dichiarazione_edilizia === "prima_1977_modifiche" ? "border-primary bg-primary/5" : "border-gray-200"
                      }`}>
                        <RadioGroupItem value="prima_1977_modifiche" id="prima_1977_modifiche" className="sr-only" />
                        <label htmlFor="prima_1977_modifiche" className="cursor-pointer">
                          <p className="font-medium">Edificata prima del 28/01/1977 con modifiche</p>
                          <p className="text-sm text-muted-foreground">Sono state effettuate modificazioni con permesso di costruire</p>
                        </label>
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${
                        formData.dichiarazione_edilizia === "sanatoria" ? "border-primary bg-primary/5" : "border-gray-200"
                      }`}>
                        <RadioGroupItem value="sanatoria" id="sanatoria" className="sr-only" />
                        <label htmlFor="sanatoria" className="cursor-pointer">
                          <p className="font-medium">Domanda di permesso in sanatoria presentata</p>
                          <p className="text-sm text-muted-foreground">Allegare copia della domanda e ricevuta pagamento oblazione</p>
                        </label>
                      </div>
                    </RadioGroup>

                    {/* Campi aggiuntivi per permesso */}
                    {(formData.dichiarazione_edilizia === "dopo_1977" || 
                      formData.dichiarazione_edilizia === "prima_1977_modifiche" ||
                      formData.dichiarazione_edilizia === "sanatoria") && (
                      <div className="grid grid-cols-3 gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                        <div className="space-y-2">
                          <Label>Comune</Label>
                          <Input 
                            value={formData.permesso_comune}
                            onChange={(e) => updateField("permesso_comune", e.target.value)}
                            placeholder="Firenze"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>N° Permesso</Label>
                          <Input 
                            value={formData.permesso_numero}
                            onChange={(e) => updateField("permesso_numero", e.target.value)}
                            placeholder="2024/001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Data</Label>
                          <Input 
                            type="date"
                            value={formData.permesso_data}
                            onChange={(e) => updateField("permesso_data", e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Dettagli Fornitura */}
            {currentStep === 5 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {formData.tipo_fornitura === "luce" ? (
                      <Zap className="w-6 h-6 text-yellow-500" />
                    ) : (
                      <Flame className="w-6 h-6 text-orange-500" />
                    )}
                    Dettagli Fornitura {formData.tipo_fornitura === "luce" ? "Elettrica" : "Gas"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {formData.tipo_fornitura === "luce" ? (
                    <>
                      {/* Tensione */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Tensione</Label>
                        <RadioGroup
                          value={formData.tensione}
                          onValueChange={(v) => updateField("tensione", v)}
                          className="flex gap-3"
                        >
                          {[
                            { value: "bassa_monofase", label: "Bassa Monofase" },
                            { value: "bassa_trifase", label: "Bassa Trifase" },
                            { value: "media", label: "Media Tensione" },
                          ].map((option) => (
                            <div key={option.value} className={`flex-1 p-3 rounded-lg border-2 text-center ${
                              formData.tensione === option.value ? "border-primary bg-primary/5" : "border-gray-200"
                            }`}>
                              <RadioGroupItem value={option.value} id={`tensione_${option.value}`} className="sr-only" />
                              <label htmlFor={`tensione_${option.value}`} className="cursor-pointer font-medium text-sm">
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>

                      {/* Tipo Fornitura */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Tipo di Fornitura</Label>
                        <Select value={formData.tipo_fornitura_dettaglio} onValueChange={(v) => updateField("tipo_fornitura_dettaglio", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ordinaria">Ordinaria</SelectItem>
                            <SelectItem value="stagionale_ricorrente">Stagionale Ricorrente</SelectItem>
                            <SelectItem value="stagionale_non_ricorrente">Stagionale Non Ricorrente</SelectItem>
                            <SelectItem value="straordinaria">Straordinaria</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Uso Fornitura */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Uso Fornitura</Label>
                        <Select value={formData.uso_fornitura} onValueChange={(v) => updateField("uso_fornitura", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="residente">Residente</SelectItem>
                            <SelectItem value="non_residente">Non Residente</SelectItem>
                            <SelectItem value="altri_usi">Altri Usi</SelectItem>
                            <SelectItem value="illuminazione_pubblica">Illuminazione Pubblica</SelectItem>
                            <SelectItem value="cantieri">Uso Cantieri</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Potenza */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Potenza Richiesta (kW)</Label>
                        <Select value={formData.potenza_richiesta} onValueChange={(v) => updateField("potenza_richiesta", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1.5">1.5 kW</SelectItem>
                            <SelectItem value="3">3 kW</SelectItem>
                            <SelectItem value="4.5">4.5 kW</SelectItem>
                            <SelectItem value="6">6 kW</SelectItem>
                            <SelectItem value="10">10 kW</SelectItem>
                            <SelectItem value="15">15 kW</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Classe Contatore Gas */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Classe Contatore</Label>
                        <RadioGroup
                          value={formData.classe_contatore}
                          onValueChange={(v) => updateField("classe_contatore", v)}
                          className="space-y-2"
                        >
                          <div className={`p-4 rounded-lg border-2 ${
                            formData.classe_contatore === "g4" ? "border-primary bg-primary/5" : "border-gray-200"
                          }`}>
                            <RadioGroupItem value="g4" id="g4" className="sr-only" />
                            <label htmlFor="g4" className="cursor-pointer flex justify-between">
                              <div>
                                <p className="font-medium">Fino a G4</p>
                                <p className="text-sm text-muted-foreground">Uso domestico standard</p>
                              </div>
                              <span className="text-green-600 font-semibold">€30</span>
                            </label>
                          </div>
                          <div className={`p-4 rounded-lg border-2 ${
                            formData.classe_contatore === "g6" ? "border-primary bg-primary/5" : "border-gray-200"
                          }`}>
                            <RadioGroupItem value="g6" id="g6" className="sr-only" />
                            <label htmlFor="g6" className="cursor-pointer flex justify-between">
                              <div>
                                <p className="font-medium">G6</p>
                                <p className="text-sm text-muted-foreground">Uso domestico elevato</p>
                              </div>
                              <span className="text-green-600 font-semibold">€30</span>
                            </label>
                          </div>
                          <div className={`p-4 rounded-lg border-2 ${
                            formData.classe_contatore === "superiore_g6" ? "border-primary bg-primary/5" : "border-gray-200"
                          }`}>
                            <RadioGroupItem value="superiore_g6" id="superiore_g6" className="sr-only" />
                            <label htmlFor="superiore_g6" className="cursor-pointer flex justify-between">
                              <div>
                                <p className="font-medium">Superiore a G6</p>
                                <p className="text-sm text-muted-foreground">Uso commerciale/industriale</p>
                              </div>
                              <span className="text-amber-600 font-semibold">€45</span>
                            </label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Uso Gas */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Uso del Gas</Label>
                        <Select value={formData.uso_gas} onValueChange={(v) => updateField("uso_gas", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cottura_acqua">Cottura cibi e acqua calda</SelectItem>
                            <SelectItem value="riscaldamento">Riscaldamento</SelectItem>
                            <SelectItem value="cottura_riscaldamento">Cottura + Riscaldamento</SelectItem>
                            <SelectItem value="uso_tecnologico">Uso tecnologico</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Periodo Fornitura */}
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Periodo di Fornitura
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Inizio</Label>
                        <Input 
                          type="date"
                          value={formData.data_inizio}
                          onChange={(e) => updateField("data_inizio", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Fine (opzionale)</Label>
                        <Input 
                          type="date"
                          value={formData.data_fine}
                          onChange={(e) => updateField("data_fine", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 6: Firma */}
            {currentStep === 6 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PenTool className="w-6 h-6 text-primary" />
                    Firma Digitale e Documenti
                  </CardTitle>
                  <CardDescription>
                    Completa la richiesta con la tua firma
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Documento */}
                  <div className="space-y-4">
                    <h4 className="font-semibold">Documento di Riconoscimento *</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo Documento</Label>
                        <Select value={formData.documento_tipo} onValueChange={(v) => updateField("documento_tipo", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="carta_identita">Carta d'Identità</SelectItem>
                            <SelectItem value="patente">Patente di Guida</SelectItem>
                            <SelectItem value="passaporto">Passaporto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Numero Documento *</Label>
                        <Input 
                          value={formData.documento_numero}
                          onChange={(e) => updateField("documento_numero", e.target.value.toUpperCase())}
                          placeholder="AA1234567"
                          className="uppercase"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Scadenza</Label>
                        <Input 
                          type="date"
                          value={formData.documento_scadenza}
                          onChange={(e) => updateField("documento_scadenza", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Firma Canvas */}
                  <div className="border-t pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">Firma *</h4>
                      <Button variant="outline" size="sm" onClick={clearSignature}>
                        Cancella Firma
                      </Button>
                    </div>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        className="w-full cursor-crosshair touch-none"
                        style={{ touchAction: "none" }}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground text-center">
                      Firma con il mouse o il dito nel riquadro sopra
                    </p>
                  </div>

                  {/* Data e Luogo */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Luogo</Label>
                      <Input 
                        value={formData.firma_luogo}
                        onChange={(e) => updateField("firma_luogo", e.target.value)}
                        placeholder="Firenze"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input 
                        type="date"
                        value={formData.firma_data}
                        onChange={(e) => updateField("firma_data", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Consensi */}
                  <div className="border-t pt-6 space-y-4">
                    <h4 className="font-semibold">Consensi</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-4 rounded-lg border bg-gray-50">
                        <Checkbox 
                          id="privacy"
                          checked={formData.consenso_privacy}
                          onCheckedChange={(c) => updateField("consenso_privacy", c)}
                          className="mt-0.5"
                        />
                        <label htmlFor="privacy" className="text-sm cursor-pointer">
                          <span className="font-medium">Accetto l'informativa sulla privacy *</span>
                          <p className="text-muted-foreground mt-1">
                            Dichiaro di aver letto e compreso l'informativa sul trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR)
                          </p>
                        </label>
                      </div>
                      <div className="flex items-start gap-3 p-4 rounded-lg border">
                        <Checkbox 
                          id="marketing"
                          checked={formData.consenso_marketing}
                          onCheckedChange={(c) => updateField("consenso_marketing", c)}
                          className="mt-0.5"
                        />
                        <label htmlFor="marketing" className="text-sm cursor-pointer">
                          <span className="font-medium">Consenso marketing (opzionale)</span>
                          <p className="text-muted-foreground mt-1">
                            Acconsento a ricevere comunicazioni commerciali e promozionali da 8Energy
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 7: Conferma */}
            {currentStep === 7 && (
              <Card className="text-center">
                <CardContent className="py-16">
                  <div className="w-24 h-24 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold mb-4 text-green-700">Richiesta Inviata!</h2>
                  <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
                    La tua richiesta di attivazione {formData.tipo_fornitura === "luce" ? "luce" : "gas"} è stata inviata con successo. 
                    Ti contatteremo presto per confermare i dettagli.
                  </p>
                  
                  {/* PDF Download Button */}
                  {requestId && (
                    <div className="mb-8">
                      <Button 
                        onClick={downloadPDF}
                        className="bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-600 text-white px-8 py-3 text-lg"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Scarica Contratto PDF
                      </Button>
                      <p className="text-sm text-muted-foreground mt-2">
                        Scarica una copia del contratto firmato
                      </p>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto mb-8">
                    <h4 className="font-semibold mb-3">Riepilogo</h4>
                    <div className="text-left space-y-2 text-sm">
                      <p><span className="text-muted-foreground">ID Richiesta:</span> <span className="font-mono">{requestId}</span></p>
                      <p><span className="text-muted-foreground">Tipo:</span> {formData.tipo_fornitura === "luce" ? "Energia Elettrica" : "Gas Naturale"}</p>
                      <p><span className="text-muted-foreground">Codice:</span> {formData.codice_pod_pdr}</p>
                      <p><span className="text-muted-foreground">Indirizzo:</span> {formData.fornitura_via} {formData.fornitura_numero}, {formData.fornitura_comune}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 justify-center">
                    <Button variant="outline" onClick={() => navigate("/dashboard")}>
                      Vai alla Dashboard
                    </Button>
                    <Button onClick={() => {
                      setFormData(initialFormData);
                      setRequestId(null);
                      setCurrentStep(1);
                    }}>
                      Nuova Richiesta
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        {currentStep < 7 && (
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="min-w-[120px]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            
            {currentStep === 6 ? (
              <Button 
                onClick={handleSubmit} 
                disabled={loading || !formData.firma_immagine || !formData.consenso_privacy}
                className="min-w-[160px] bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Invia Richiesta</>
                )}
              </Button>
            ) : (
              <Button onClick={nextStep} className="min-w-[120px]">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
